import { useEffect, useRef, useState } from 'react'
import { BRANDS, BRAND_INFO, CHANNELS, CONTENT_TYPES } from '@hub/shared'
import { PERIODS, useFilters } from '../lib/filters.js'
import { brandFill, brandSelected, brandText, channelLabel, typeLabel } from './Brand.js'
import { Icon } from './Icon.js'

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
}

/** A pill that opens a multi-select menu. Shows the choice, or the label when nothing is chosen. */
function MultiPick<T extends string>({
  label,
  options,
  selected,
  render,
  onChange,
}: {
  label: string
  options: readonly T[]
  selected: T[]
  render: (v: T) => string
  onChange: (next: T[]) => void
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const summary =
    selected.length === 0 ? label : selected.length === 1 ? render(selected[0]!) : `${label} · ${selected.length}`

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex h-9 items-center gap-1 whitespace-nowrap rounded-full px-3.5 text-small transition-colors ${
          selected.length > 0 ? 'bg-label text-cell' : 'bg-cell text-label hover:bg-fill/[0.12]'
        }`}
      >
        {summary}
        <Icon name="chevronDown" size={14} />
      </button>
      {open && (
        <div className="glass animate-menu-in absolute left-0 top-11 z-40 max-h-80 w-56 overflow-y-auto rounded-[18px] p-1.5">
          {options.map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => onChange(toggle(selected, o))}
              className="flex w-full items-center justify-between rounded-[12px] px-3 py-2 text-left text-callout hover:bg-fill/[0.12]"
            >
              {render(o)}
              {selected.includes(o) && <Icon name="check" size={16} className="text-pebro" />}
            </button>
          ))}
          {selected.length > 0 && (
            <button type="button" onClick={() => onChange([])} className="w-full px-3 py-2 text-left text-small text-pebro">
              Auswahl aufheben
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/** Shared by pipeline, calendar and tasks. `period: false` hides the time filter (calendar pages by month). */
export function FilterBar({ period = true, types = true }: { period?: boolean; types?: boolean }): JSX.Element {
  const { filters, update, reset, active } = useFilters()
  const [q, setQ] = useState(filters.q)
  // Phone: search plus a "Filter" toggle; the rest folds away. Opens by itself when filters are set.
  const [expanded, setExpanded] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)

  // Mac: "/" jumps to the search, as in Finder-like apps and most web tools.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const t = e.target as HTMLElement
      if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName)) {
        e.preventDefault()
        searchRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Debounced search: the list follows the typing without a request per key.
  useEffect(() => {
    const t = setTimeout(() => {
      if (q !== filters.q) update({ q })
    }, 250)
    return () => clearTimeout(t)
  }, [q, filters.q, update])

  const filterCount = active - (filters.q ? 1 : 0)
  const searchField = (
    <label className="flex h-9 min-w-[10rem] flex-1 items-center gap-2 rounded-full bg-cell px-3.5 text-label2/60 md:max-w-xs">
      <Icon name="search" size={16} />
      <input
        ref={searchRef}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Suchen"
        enterKeyHint="search"
        className="w-full bg-transparent text-small text-label outline-none placeholder:text-label2/50"
      />
      {q && (
        <button type="button" aria-label="Suche leeren" onClick={() => setQ('')}>
          <Icon name="close" size={14} />
        </button>
      )}
    </label>
  )

  return (
    <div className="space-y-2 md:space-y-0">
      <div className="flex items-center gap-2 md:hidden">
        {searchField}
        <button
          type="button"
          onClick={() => setExpanded((x) => !x)}
          aria-expanded={expanded}
          className={`flex h-9 shrink-0 items-center gap-1.5 rounded-full px-3.5 text-small ${filterCount > 0 ? 'bg-label font-medium text-cell' : 'bg-cell text-label'}`}
        >
          <Icon name="filter" size={16} />
          Filter{filterCount > 0 && <span className="num">{filterCount}</span>}
        </button>
      </div>
    <div className={`${expanded ? 'flex' : 'hidden'} flex-wrap items-center gap-2 md:flex`}>
      <div className="flex max-w-full items-center gap-1 overflow-x-auto rounded-full bg-cell p-1 [scrollbar-width:none]">
        {BRANDS.map((b) => {
          const on = filters.brand.includes(b)
          return (
            <button
              key={b}
              type="button"
              onClick={() => update({ brand: toggle(filters.brand, b) })}
              aria-pressed={on}
              className="flex h-7 shrink-0 items-center gap-1.5 rounded-full px-3 text-small font-medium transition-colors"
              style={on ? brandSelected(b) : { color: brandText(b) }}
            >
              {on ? <Icon name="check" size={13} /> : <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: brandFill(b) }} />}
              {BRAND_INFO[b].name}
            </button>
          )
        })}
      </div>

      <MultiPick label="Kanal" options={CHANNELS} selected={filters.channel} render={channelLabel} onChange={(channel) => update({ channel })} />
      {types && <MultiPick label="Art" options={CONTENT_TYPES} selected={filters.type} render={typeLabel} onChange={(type) => update({ type })} />}

      {period && (
        <div className="flex max-w-full items-center overflow-x-auto rounded-full bg-cell p-1 [scrollbar-width:none]">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => update({ period: p.id })}
              className={`h-7 shrink-0 whitespace-nowrap rounded-full px-3 text-small transition-colors ${
                filters.period === p.id ? 'bg-label font-medium text-cell' : 'text-label2/70 hover:text-label'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}

      <div className="hidden flex-1 md:flex">{searchField}</div>

      {active > 0 && (
        <button
          type="button"
          onClick={() => {
            setQ('')
            reset()
          }}
          className="h-9 px-2 text-small text-pebro"
        >
          Zurücksetzen
        </button>
      )}
    </div>
    </div>
  )
}
